import {html, nothing, svg, type HTMLTemplateResult} from "lit";
import {formatTime, pad2} from "../../../../domain/gimmicks/stopwatch.state";
import {arcPath, polarPoint, type Point} from "../../svg-geometry";
import type {SkinInput} from "./skin-input";

const R = 84;

/**
 * The filled part of the ring as an SVG arc from 12 o'clock clockwise to `fraction` of the circle — pure
 * geometry in `d`, so how it is stroked stays in ralphy.css (no `stroke-dasharray` in the template).
 * `null` at zero: an arc to its own start point draws nothing anyway. The geometry is shared with the picker's
 * looks (`svg-geometry.ts`, feature plan 0072).
 */
export function ringArcPath(fraction: number): string | null {
    return arcPath(100, 100, R, 0, fraction);
}

/** The point `fraction` of the way round the ring, clockwise from 12 o'clock — the arc's end and the dot. */
export function ringPoint(fraction: number): Point {
    return polarPoint(100, 100, R, fraction);
}

/** The ring fills by `fraction` when given (the timer: time left of the duration), otherwise by the seconds of the
 *  current minute (the stopwatch). */
export function renderRingSkin({ms, parts, running, fraction: given}: SkinInput): HTMLTemplateResult {
    const fraction = given ?? (ms % 60000) / 60000;
    const dot = ringPoint(fraction);
    const arc = ringArcPath(fraction);
    const main = formatTime(parts, false);
    const timeClass = parts.h > 0 ? 'stopwatch-ring__time stopwatch-ring__time--long' : 'stopwatch-ring__time';

    return html`
        <svg class="stopwatch-ring" viewBox="0 0 200 200" aria-hidden="true" data-running=${running ? 'true' : 'false'}>
            ${svg`
                <circle class="stopwatch-ring__disc" cx="100" cy="100" r="98"></circle>
                <circle class="stopwatch-ring__track" cx="100" cy="100" r=${R}></circle>
                ${arc === null ? nothing : svg`<path class="stopwatch-ring__progress" d=${arc}></path>`}
                <circle class="stopwatch-ring__dot" cx=${dot.x.toFixed(2)} cy=${dot.y.toFixed(2)} r="6"></circle>
                <text class=${timeClass} x="100" y="108">${main}</text>
                <text class="stopwatch-ring__hundredths stopwatch__hundredths" x="100" y="140">${pad2(parts.cs)}</text>
            `}
        </svg>
    `;
}
